import { useMemo } from 'react';
import { getVisibleCountries } from '../utils/tableHelpers';
import type { Product } from '../pages/ProductMatrixNew';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface Tab {
  id: string;
  label: string;
  count: number;
  description?: string;
}

interface Level2PlatformData {
  platform_description: string;
  products: Product[];
}

interface ProductProcessingParams {
  platformProducts: Product[];
  selectedPlatform: string | null;
  tabSearchQuery: string;
  products: Product[];
  tabs?: Tab[]; // Optional to handle circular dependency with tab generation
  COUNTRIES: Country[];
  selectedCountry: string | null;
  currentView: 'selection' | 'platform' | 'location' | 'onboarding';
  browseMode: boolean;
}

interface ProductProcessingResult {
  // Filtered products
  hardwareProducts: Product[];
  softwareProducts: Product[];

  // Country visibility
  unifiedVisibleCountries: Country[];
  searchVisibleCountries: Country[];
  useCompactMode: boolean;

  // Level 2 platforms
  level2Platforms: Record<string, Level2PlatformData>;
  level2SearchResults: Record<string, Level2PlatformData>;

  // Computed values
  platformDescription: string;
  groupedSolutions: Record<string, Product[]>;
  showPlatformSelection: boolean;
  showMainLanding: boolean;
}

/**
 * Custom hook for processing product data
 * Extracted from ProductMatrixNew.tsx to reduce component complexity
 *
 * Handles:
 * - Product filtering and deduplication
 * - Country visibility calculations
 * - Level 2 platform grouping
 * - Search results processing
 */
export const useProductProcessing = (params: ProductProcessingParams): ProductProcessingResult => {
  const {
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products,
    tabs,
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode
  } = params;

  return useMemo(() => {
    // 1. PRODUCT FILTERING & DEDUPLICATION

    // Filter hardware products
    const filteredHardwareProducts = platformProducts.filter(product => {
      const basicFilter = product.category === 'Hardware' &&
        String(product.solution_level) !== "2" &&
        product.platform === selectedPlatform;

      if (!basicFilter) return false;

      // If there's a search query, search within hardware products of this platform only
      if (tabSearchQuery.trim()) {
        const query = tabSearchQuery.toLowerCase();
        const searchableText = [
          product.solution,
          product.solution_description,
          product.hardware_family,
          product.model,
          product.tag,
          product.notes
        ].filter(Boolean).join(' ').toLowerCase();

        return searchableText.includes(query);
      }

      return true;
    });

    // Deduplicate hardware products by unique combination of solution + hardware_family + model
    const hardwareProducts = filteredHardwareProducts.filter((product, index, self) =>
      index === self.findIndex(p =>
        p.solution === product.solution &&
        p.hardware_family === product.hardware_family &&
        p.model === product.model
      )
    );

    // Filter software products
    const filteredSoftwareProducts = platformProducts.filter(product => {
      const basicFilter = product.category !== 'Hardware' &&
        String(product.solution_level) !== "2" &&
        product.platform === selectedPlatform;

      if (!basicFilter) return false;

      // If there's a search query, search within software products of this platform only
      if (tabSearchQuery.trim()) {
        const query = tabSearchQuery.toLowerCase();
        const searchableText = [
          product.solution,
          product.solution_description,
          product.tag,
          product.vertical,
          product.notes
        ].filter(Boolean).join(' ').toLowerCase();

        return searchableText.includes(query);
      }

      return true;
    });

    // Deduplicate software products by solution name to avoid duplicate rows
    const softwareProducts = filteredSoftwareProducts.filter((product, index, self) =>
      index === self.findIndex(p => p.solution === product.solution)
    );

    // Extract platform description
    const platformDescription = platformProducts.length > 0 ? platformProducts[0].platform_description || '' : '';

    // 2. UNIFIED COUNTRY VISIBILITY CALCULATION

    // Calculate visible countries based on ALL products (hardware + software)
    const allPlatformProducts = [...hardwareProducts, ...softwareProducts];
    let unifiedVisibleCountries = getVisibleCountries(allPlatformProducts, COUNTRIES);

    // Special filtering for SkyTab Venue: only show countries with at least one true value in SOFTWARE products
    // This ensures Product Modules tab determines which countries are visible across all tabs
    if (selectedPlatform === 'SkyTab Venue') {
      unifiedVisibleCountries = unifiedVisibleCountries.filter(country =>
        softwareProducts.some(product => product.countries[country.code] === true)
      );
    }

    // Determine if we should use compact mode for tables with many columns
    const useCompactMode = unifiedVisibleCountries.length >= 14;

    // 3. LEVEL 2 PLATFORM GROUPING

    // Group software products by solution level
    const groupedSolutions = softwareProducts.reduce((acc, product) => {
      const level = String(product.solution_level) || "1";
      if (!acc[level]) {
        acc[level] = [];
      }
      acc[level].push(product);
      return acc;
    }, {} as Record<string, Product[]>);

    // Group Level 2 products by their platform (for separate sections)
    const level2PlatformsRaw = platformProducts
      .filter(product => String(product.solution_level) === "2")
      .reduce((acc, product) => {
        const platformName = product.platform;
        if (!acc[platformName]) {
          acc[platformName] = {
            platform_description: product.platform_description || '',
            products: []
          };
        }
        acc[platformName].products.push(product);
        return acc;
      }, {} as Record<string, Level2PlatformData>);

    // Deduplicate Level 2 products by solution name within each platform
    const level2Platforms = Object.entries(level2PlatformsRaw).reduce((acc, [platformName, platformData]) => {
      const deduplicatedProducts = platformData.products.filter((product, index, self) =>
        index === self.findIndex(p => p.solution === product.solution)
      );
      acc[platformName] = {
        platform_description: platformData.platform_description,
        products: deduplicatedProducts
      };
      return acc;
    }, {} as Record<string, Level2PlatformData>);

    // Get Shift4 Payments products (they are level 1, not level 2)
    const paymentsProducts = platformProducts.filter(p => p.platform === 'Shift4 Payments');
    const paymentsPlatformData = paymentsProducts.length > 0 ? {
      platform_description: paymentsProducts[0].platform_description || 'Payment processing services',
      products: paymentsProducts.filter((product, index, self) =>
        index === self.findIndex(p => p.solution === product.solution)
      )
    } : null;

    // Add Shift4 Payments to level2Platforms for easier access
    if (paymentsPlatformData) {
      level2Platforms['Shift4 Payments'] = paymentsPlatformData;
    }

    // 4. SEARCH RESULTS PROCESSING

    let level2SearchResults: Record<string, Level2PlatformData> = {};
    let searchVisibleCountries = unifiedVisibleCountries;

    if (tabSearchQuery.trim() && selectedPlatform !== 'Integrated Payments' && tabs && tabs.length > 0) {
      const query = tabSearchQuery.toLowerCase();

      // Get tab IDs for level2 platforms in the current platform
      const level2TabIds = tabs
        .filter(tab => tab.id.startsWith('level2-'))
        .map(tab => tab.id);

      Object.entries(level2Platforms).forEach(([platformName, platformData]) => {
        // Only include level2 platforms that have tabs in the current platform
        const tabId = `level2-${platformName.toLowerCase().replace(/\s+/g, '-')}`;
        if (!level2TabIds.includes(tabId)) {
          return; // Skip platforms that don't have tabs in this platform
        }

        const matchingProducts = platformData.products.filter(product => {
          const searchableText = [
            product.solution,
            product.solution_description,
            product.platform,
            product.category,
            product.notes
          ].filter(Boolean).join(' ').toLowerCase();
          return searchableText.includes(query);
        });

        if (matchingProducts.length > 0) {
          level2SearchResults[platformName] = {
            platform_description: platformData.platform_description,
            products: matchingProducts
          };
        }
      });

      // Calculate visible countries for search results including level2 products
      const allLevel2SearchProducts = Object.values(level2SearchResults).flatMap(p => p.products);
      const allSearchProducts = [...hardwareProducts, ...softwareProducts, ...allLevel2SearchProducts];
      searchVisibleCountries = getVisibleCountries(allSearchProducts, COUNTRIES);

      // Special filtering for SkyTab Venue
      if (selectedPlatform === 'SkyTab Venue') {
        searchVisibleCountries = searchVisibleCountries.filter(country =>
          allSearchProducts.some(product => product.countries[country.code] === true)
        );
      }
    }

    // 5. COMPUTED VIEW FLAGS

    const showPlatformSelection = !!(selectedCountry && currentView === 'selection');
    const showMainLanding = currentView === 'selection' && browseMode && !selectedCountry;

    return {
      // Filtered products
      hardwareProducts,
      softwareProducts,

      // Country visibility
      unifiedVisibleCountries,
      searchVisibleCountries,
      useCompactMode,

      // Level 2 platforms
      level2Platforms,
      level2SearchResults,

      // Computed values
      platformDescription,
      groupedSolutions,
      showPlatformSelection,
      showMainLanding
    };
  }, [
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products,
    tabs,
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode
  ]);
};
